import React, { useEffect, useRef, useState } from 'react'
import { format, sub } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'
import {
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@chakra-ui/react'
import { collections } from '@homevest/utils'

import axios from 'lib/axios'
import { formatMoney } from 'lib/numbers'
import { getPayOrQuitApiError } from './payOrQuitMessages'

export { getPayOrQuitApiError } from './payOrQuitMessages'

type PayOrQuitRun = {
  id: string
  status: string
  dispatch_completed_at: string | null
  notice_date?: string | null
}

type StartPayOrQuitRunResponse = {
  run: PayOrQuitRun
  resumed: boolean
  archive_tracked: boolean
}

type PayOrQuitSendModalProps = {
  addressLine1?: string | null
  balance?: number | null
  canSend: boolean
  isOpen: boolean
  onClose: () => void
  rentalId: string
  state: string
}

export const payOrQuitRequestStorageKey = (rentalId: string) =>
  `pay-or-quit-send-request:${rentalId}`

const PayOrQuitSendModal = ({
  addressLine1,
  balance,
  canSend,
  isOpen,
  onClose,
  rentalId,
  state
}: PayOrQuitSendModalProps) => {
  const toast = useToast()
  const requestKeyRef = useRef<string | null>(null)
  const [isSure, setIsSure] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const followupConfig = collections.PAY_OR_QUIT_FOLLOWUP_DATES[state]
  const expirationDate = collections.getPayOrQuitFollowupDate(state, sub(new Date(), { days: 1 }))

  useEffect(() => {
    if (isOpen) {
      requestKeyRef.current = window.sessionStorage.getItem(payOrQuitRequestStorageKey(rentalId))
    } else {
      requestKeyRef.current = null
      setIsSure(false)
    }
  }, [isOpen, rentalId])

  const handleSubmit = async () => {
    const storageKey = payOrQuitRequestStorageKey(rentalId)
    const idempotencyKey =
      requestKeyRef.current ?? window.sessionStorage.getItem(storageKey) ?? uuidv4()
    requestKeyRef.current = idempotencyKey
    window.sessionStorage.setItem(storageKey, idempotencyKey)
    setIsSending(true)

    try {
      const { data } = await axios.post<StartPayOrQuitRunResponse>('/admin/pay-or-quit/runs', {
        rental_id: rentalId,
        idempotency_key: idempotencyKey
      })

      const needsAttention = data.archive_tracked && data.run.status === 'needs_attention'
      if (!data.archive_tracked || (data.run.dispatch_completed_at && !needsAttention)) {
        window.sessionStorage.removeItem(storageKey)
      }
      onClose()
      toast({
        title: needsAttention
          ? 'Pay or Quit needs attention'
          : !data.archive_tracked
          ? 'Pay or Quit sent'
          : data.resumed
          ? 'Send request resumed'
          : 'Send started',
        description: needsAttention
          ? 'Contact Engineering to review this send before trying again, to avoid duplicate emails.'
          : 'The email request has been submitted. Generating notices for posting is a separate action.',
        status: needsAttention ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom'
      })
    } catch (error) {
      toast({
        title: 'Could not start the Pay or Quit send',
        description: getPayOrQuitApiError(error),
        status: 'error',
        duration: 10000,
        isClosable: true,
        position: 'bottom'
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>Send Pay or Quit to {addressLine1}</ModalHeader>
        <ModalBody>
          <Text>
            Pay or Quit Balance: <strong>{formatMoney(balance, 2, '$')}</strong>
          </Text>
          {followupConfig && expirationDate ? (
            <Text my={2}>
              Since this home is in {state}, we have to give a {followupConfig.days - 1}{' '}
              {followupConfig.type === 'business' ? 'business' : ''} day notice for pay or quits.
              This means the new pay or quit will expire on{' '}
              <strong>{format(expirationDate, 'MM/d/yyyy')}</strong>.
            </Text>
          ) : (
            <Text my={2}>The notice period will follow the rules configured for this home.</Text>
          )}
          {canSend ? (
            <Checkbox
              isChecked={isSure}
              onChange={(event) => setIsSure(event.target.checked)}
              isInvalid
              colorScheme='red'
              color='red'
            >
              Sure you want to send?
            </Checkbox>
          ) : (
            <Text color='red'>
              You do not have the necessary permissions to send new pay or quits.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button colorScheme='gray' onClick={onClose} isDisabled={isSending}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={handleSubmit}
              isDisabled={!canSend || !isSure || isSending}
              isLoading={isSending}
            >
              Send
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PayOrQuitSendModal
